import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

/**
 * Slide-in filter drawer (mobile): opaque themed panel (MB) + full-viewport RS scrim.
 * Overlay and panel are sibling nodes portaled to document.body (RS blur never wraps MB).
 */
export default function FilterDrawer({ isOpen, onClose, children, title = 'Filtreler' }: FilterDrawerProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="sb-drawer-overlay"
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        className="sb-drawer-panel sb-drawer-panel--left sb-drawer-panel--open"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="sb-drawer-panel-header d-flex align-items-center justify-content-between px-3 py-3 border-bottom border-secondary-subtle">
          <span className="fw-bold text-primary">
            <i className="fas fa-filter me-2" />
            {title}
          </span>
          <Button
            type="button"
            className="btn btn-link p-2"
            style={{ color: 'var(--sb-text)' }}
            onClick={onClose}
            aria-label="Filtreyi kapat"
          >
            <i className="fas fa-times fa-lg" />
          </Button>
        </header>
        <div className="sb-drawer-panel-body overflow-auto p-3 d-flex flex-column flex-grow-1 min-h-0">
          {children}
        </div>
      </aside>
    </>,
    document.body
  );
}
